import ISDKDriver from './ISDKDriver';
import Game from 'Game';
import { AdvertisementType, IAdvertisementPlacement } from '../Advertisement';
import { defaultLanguageKey, LanguageKey } from '../Language';
import IPlayerData, { playerDataKeysToLoad } from 'Player/IPlayerData';
import * as A2U from '@sgorg/app-to-user-client';
import { PlayerActions } from 'State/Player/PlayerActions';
import { FBTrackingEvent } from '../../Tracking/FBTrackingEvent';
import { sendEvent, track } from '../../Tracking/Track';
import { Leaderboard } from 'System/UI/LeaderboardHelper';
import { EntryPointType } from '../../EntryPoint/EntryPointType';
import { Random } from '../../Random';
import DateHelper from '../../DateHelper';
import { AdType } from '../../Tracking/DataSet/Ad';
import { trackDisplayedAdvertisement, trackRequestAdvertisement } from '../../Tracking/TrackingHelpers/Ad';
import { RewardType } from '../../RewardHelper';
import { GBCHandler } from '../GBCHandler';
import { FTUXTrackingEventName } from '../../Tracking/TrackingEvent';
import { Deferred } from '../../Deferred';
import { sleep } from '../../async';
import { getFBInstant, isAPISupported } from './FBHelpers';
import { FBPayments } from './FBPayments';
import { IGameState } from 'State/Game/IGameState';
import { IScoreEntry } from './IScoreEntry';
import { IMessagePayload } from 'State/Game/IEntryPoint';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';
import { decryptPayload } from '../../EntryPoint/EntryPointEncryption';
import { EncryptedEntryPoint } from '../../EntryPoint/EntryPointHelper';
import { LoadEvents, LoadEventType, LoadtimeTrackerSingleton } from '../../LoadtimeTracker';
import { FBInstantSaver, GameBackendSaver, LocalStorageSaver, Privileges, SaveHandler } from '@sgorg/saver';
import { isIOS } from '../../Platform';
import { PayloadType } from '../PayloadType';
import PayloadHelper from '../PayloadHelper';
import { SocialActionTypes } from '../../Tracking/DataSet/SocialAction';
import { getLatestData, handleSaverError, saveHandlerLogger } from '../../SaveHandlers/SaveHandlerHelper';
import { setUserData } from '@sgorg/game-backend-client';
import { FBContextHandler } from './FBContextHandler';
import { AuthPlatform } from '@sgorg/mobile-dev-tools';
import { IAuthReturnValues } from 'State/Game/Action/SignIn';

declare const FBInstant: any;

declare global {
    interface Window {
        getUserId: () => string;
    }
}

const A2UTimes: Map<string, number> = new Map<string, number>([
    //['now', 0],
    ['5h', DateHelper.Hour * 5],
    ['24h', DateHelper.Day],
    ['48h', DateHelper.Day * 2],
    ['72h', DateHelper.Day * 3],
]);

interface FBInterstitialAdOrRewardedVideo {
    loadAsync: () => Promise<any>;
    showAsync: () => Promise<any>;
}

export default class FBSDKDriver implements ISDKDriver {

    private A2UClient: any;

    private user: {
        id: string;
        name: string;
        avatar: string;
    };
    private gbUserId: string;
    private readonly gbUserProfile = new Deferred<GBCHandler.IGBUserProfile>();
    private settings: {
        locale: string;
        platform: string;
        sdkVersion: string;
    };
    private entryPoint: any;
    private entryPointData: {
        senderId: string;
        senderLevel: number;
    };
    private payload: any;

    private _isSubscribedToBot: boolean;

    public readonly initialized: Promise<void>;

    private readonly botMessageId = new Deferred<string>();

    private readonly fbPayments = new FBPayments();

    private saveHandler: SaveHandler<IPlayerData>;
    private _debugGBSaver: GameBackendSaver<IPlayerData>;

    constructor() {
        this.debugExpose();
        this.initialized = this.init();

        this._debugGBSaver = new GameBackendSaver<IPlayerData>({
            toPlainObject: GameBackendSaver.toPlainObject,
            setUserData: setUserData,
        });
        this._debugGBSaver.setLogger(saveHandlerLogger);
    }

    private debugExpose() {
        if (!window) return;
        window.getUserId = () => this.getUserId();
    }

    private async init() {
        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.FB_preStartGame, LoadEventType.Start);
        await getFBInstant();
        await FBInstant.initializeAsync();

        this.saveHandler = new SaveHandler({
            drivers: [
                new LocalStorageSaver({
                    getUserId: FBInstant.player.getID,
                }),
                new FBInstantSaver({
                    setDataAsync: FBInstant.player.setDataAsync,
                    flushDataAsync: FBInstant.player.flushDataAsync,
                    getDataAsync: FBInstant.player.getDataAsync,
                }),
            ],
            logger: saveHandlerLogger,
            getLatestData: getLatestData,
            handleError: handleSaverError,
        });

        console.info('FBInstant Initialized');
        this.gameTracking(FBTrackingEvent.FTUE_00_FBInit);

        const serverTime = await this.getServerTimestamp();
        Game.serverTimeOffset = serverTime - Date.now();

        this.settings = {
            locale: this.parseLocale(FBInstant.getLocale()),
            platform: FBInstant.getPlatform(),
            sdkVersion: FBInstant.getSDKVersion(),
        };
        this.user = {
            id: FBInstant.player.getID(),
            name: FBInstant.player.getName(),
            avatar: FBInstant.player.getPhoto(),
        };

        await FBContextHandler.initialize(
            FBInstant.context.getID,
            FBInstant.context.getPlayersAsync,
            FBInstant.context.getType,
            FBInstant.context.chooseAsync,
            FBInstant.context.createAsync
        );

        this.hideConnectingNotice();

        this.saveHandler.setSavePrivileges(Privileges.PERMITTED);

        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.FB_preStartGame, LoadEventType.End);

        const initGBPromise = this.initGB().catch((error: unknown) => {
            console.warn('GBC failed to initialize', error);
        });

        this.handleGBInitPromise(initGBPromise);

        // console.log('Supported FB APIs:', FBInstant.getSupportedAPIs())
    }

    private parseLocale(locale: string): string {
        if (locale === 'pt_BR') {
            return LanguageKey.BRAZILIAN;
        }
        return locale.substr(0, 2);
    }

    private async handleGBInitPromise(initGBPromise: Promise<void>): Promise<void> {
        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.GB_loginFlow, LoadEventType.Start);
        await initGBPromise;
        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.GB_loginFlow, LoadEventType.End);

        const playerState = PlayerStateHelper.setGBCUserID(Game.store.getState().playerState, this.getGBUserId());

        if (!playerState.FBEvents.includes(FBTrackingEvent.FTUE_01_GbcInit)) {
            this.gameTracking(FBTrackingEvent.FTUE_01_GbcInit);
        }

        PlayerActions.setPlayerState(playerState);
    }

    private async getServerTimestamp(): Promise<number> {
        try {
            const playerInfo = await FBInstant.player.getSignedPlayerInfoAsync();
            return JSON.parse(atob(playerInfo.getSignature().split('.')[1])).issued_at * 1000;
        } catch (e) {
            console.warn(e);
        }

        return Date.now();
    }

    private hideConnectingNotice() {
        if (!document) return;
        const element = document.getElementById('gbMsg');
        if (!element) return;
        element.style.visibility = 'hidden';
    }

    public async initDDNA() {
        track('userID', this.getUserId());

        const state: IGameState = Game.store.getState();
        if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_01_GbcInit)) {
            track('stepId', 1);
            track('stepName', FBTrackingEvent.FTUE_01_GbcInit);
            sendEvent(FTUXTrackingEventName.ftux, Game.store.getState());
            PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_01_GbcInit);
        }
    }

    private async initGB() {
        const AUTH_DATA_KEY = 'authData';

        const gameId = Game.config.gameID;
        console.log(`Game ID: ${gameId}`);

        const authData = await GBCHandler.login({
            // FIXME: use correct gameID for dev environment
            // registering with prod gameID in dev environment will fail
            // and all the DDNA tracking will fail due to missing userID
            gameId,
            cacheRead: () => readKeyFromFB(AUTH_DATA_KEY),
            cacheWrite: data => saveKeyToFB(AUTH_DATA_KEY, data, this),
            register: async () => {
                // TODO: cache between retries (not sessions)?
                const playerInfo = await FBInstant.player.getSignedPlayerInfoAsync();
                const signature = playerInfo.getSignature();
                return await GBCHandler.registerWithFacebookInstantGameSignature({
                    gameId,
                    signature,
                });
            },
        });

        this.gbUserId = authData.userId;

        await GBCHandler.onLogin({
            syncFriendsList: this.syncFriendsList,
            userProfile: this.gbUserProfile,
            botMessageId: this.botMessageId,
        });

        return;

        async function saveKeyToFB(key: string, value: unknown, ctx: FBSDKDriver) {
            await ctx.saveFB({ [key]: value });
        }

        async function readKeyFromFB<T = unknown>(key: string): Promise<T | undefined> {
            const dataPerKey = await FBInstant.player.getDataAsync([key]);
            return dataPerKey[key];
        }
    }

    private async syncFriendsList() {
        const friendList: string[] = await Game.sdk.getFriends();

        GBCHandler.syncFriendListByPlatformPlayerId({ friendList });
    }

    public async getFriends(): Promise<string[]> {
        const fbFriends: Array<{
            getID(): string;
        }> = await FBInstant.player.getConnectedPlayersAsync();
        const friendList = fbFriends.map(friendData => friendData.getID());
        return friendList;
    }

    public async save(data: Partial<IPlayerData>, saveToGBC: boolean = true) {
        if (saveToGBC) {
            // Save to game-backend for reporting only. Do not await.
            // GBCHandler.updateGBSaveData(data);
            this._debugGBSaver.save(data);
        }

        // Save to Facebook.
        return this.saveFB(data);
    }

    public async flushSaveData(): Promise<unknown> {
        // Handled by FBInstantSaver
        return;
    }

    public async restore(): Promise<IPlayerData> {
        // Read only from Facebook.
        // Handled by FBInstantSaver
        return this.restoreFB();
    }

    // EMPTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public async levelStart(level: number) {}

    // EMPTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public async levelFinish(level: number, score: number, complete: boolean) {}

    // EMPTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public async playWithFriends(text: string, imageBase64: string, payload: any, friendId?: string) {}

    public async customUpdate(
        text: string,
        imageBase64: string,
        friendId: string,
        payload: any,
        strategy?: string,
        cta?: string
    ) {
        const customMessage = await FBInstant.updateAsync({
            action: 'CUSTOM',
            cta: cta || 'Play',
            image: imageBase64,
            text: {
                default: text,
                localizations: {
                    en_US: text,
                },
            },
            template: 'default',
            data: payload,
            strategy: strategy || 'IMMEDIATE',
        });

        return customMessage;
    }

    public async loadComplete() {
        const handle = FBInstant.player.getName();
        const locale = this.parseLocale(FBInstant.getLocale());

        if (this.settings.locale !== locale) {
            this.settings.locale = locale;
        }

        this.gbUserProfile.control.resolve({
            handle: handle || '',
            locale,
        });

        await FBInstant.startGameAsync();
        track('sdkVersion', FBInstant.getSDKVersion());

        const state: IGameState = Game.store.getState();
        if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_03_GameplayShown)) {
            this.gameTracking(FBTrackingEvent.FTUE_03_GameplayShown);
            PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_03_GameplayShown);

            track('stepId', 3);
            track('stepName', FBTrackingEvent.FTUE_03_GameplayShown);
            sendEvent(FTUXTrackingEventName.ftux, Game.store.getState());
        }

        /* const entryPointData = {
            entryPoint: 'adCampaign',

            payload: {
                origin: 'marketing_campaign', // fixed value

                mAdType: 'traffic', // Can be any string. Some examples: acquisition, reengagement, installs, traffic...

                mAdName: 'NameOfCampaign', // any possible string

                mChannel: 'ChannelName', // any possible string

                mAdID: '123123123', // if the user comes from a FB Ad, it will show the Ad Id; if not: null
            },
            messageId: undefined as any,
        }; */

        const entryPointData = this.getDecryptedEntryPointData();

        // Bot message conversion tracking via GBC.

        if (entryPointData) {
            this.setEntryPointData(entryPointData);
            //track('entryPoint', entryPointData);

            if (entryPointData.entryPoint === EntryPointType.BOT && entryPointData.messageId) {
                this.botMessageId.control.resolve(entryPointData.messageId);
            }
        }

        track('customEntryPoint', (entryPointData && entryPointData.customEntryPoint) || 'ORGANIC');

        const contextType = FBContextHandler.getContextType();
        if (contextType) track('epContext', contextType);

        const entryPoint = await FBInstant.getEntryPointAsync();
        if (entryPoint) {
            if (this.entryPoint) {
                this.entryPoint.async = entryPoint;
            }
            track('entryPoint', entryPoint);
        }

        if (this.entryPoint) console.info('Entry Point:', this.entryPoint);


    }

    private getDecryptedEntryPointData(): any {
        const entryPointData = FBInstant.getEntryPointData();

        if (!entryPointData) {
            return null;
        }

        if ((entryPointData as EncryptedEntryPoint).enc) {
            const payload = decryptPayload(entryPointData);

            if (!payload) return null;
            else if (typeof payload.recipientId === 'undefined') return payload;
            else if (payload.recipientId !== FBInstant.player.getID()) return null;

            return payload;
        }

        return entryPointData;
    }

    private async setEntryPointData(data: any) {
        if (data === null) return;
        this.entryPoint = data;
        this.payload = data;

        // If the entry point has a sender, track the sender
        if (this.entryPoint.userId != undefined) {
            track('epSenderId', this.entryPoint.userId);

            // If the current user is not the sender, get the senders progress
            if (this.entryPoint.userId !== this.getUserId()) {
                this.getFriendsScores(Leaderboard.TOTAL_LEVELS, 'default').then(entries => {
                    const playerEntry: IScoreEntry = entries.find(
                        (entry: IScoreEntry) => entry.user.playerId === this.entryPoint.userId
                    );

                    if (playerEntry == undefined) return;

                    track('epSenderLevel', playerEntry.score);
                    this.entryPoint.userScore = playerEntry.score;
                });
            }
        }

        if (this.entryPoint.timestamp) {
            track('epTimestamp', this.entryPoint.timestamp);
        }
    }

    public getAvatar(): string {
        if (this.user && this.user.avatar) {
            return this.user.avatar;
        }

        return 'no_avatar';
    }

    public getPayload(): any {
        return this.payload;
    }

    public getEntryPointData(): string {
        return this.entryPoint;
    }

    public getContextType(): string {
        return FBContextHandler.getContextType();
    }

    public getContextID(): string {
        return FBContextHandler.getContextID();
    }

    public isPlayerInContext(id: string): boolean {
        return FBContextHandler.isPlayerInContext(id);
    }

    public getUserId(): string {
        return "1";
        if (!this.user || !this.user.id) {
            throw new Error('User ID not found.');
        }
        return this.user.id;
    }

    public getGBUserId(): string {
        if (!this.gbUserId) {
            console.warn('User ID not found.');
        }
        return this.gbUserId;
    }

    public getUsername(): string {
        if (!this.user || !this.user.name) {
            return 'Unknown';
        }
        return this.user.name;
    }

    public async loadingProgress(progress: number) {
        progress = Math.floor(progress);
        if (progress >= 99) progress = 100;

        FBInstant.setLoadingProgress(progress);
    }

    // DIRTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    // private handler: any;
    public async onStart() {
        //this.handler.modules.start();
    }

    public async runQuery(query: string, params: any) {
        console.warn("'runQuery' is depreciated");
    }

    // DIRTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public runGame(): void {
        //Game.start();
    }

    public freezeGame(): void {
        Game.ticker.stop();
    }

    public unfreezeGame(): void {
        Game.ticker.start();
    }

    public async getFriendsScores(leaderboardName: string, instanceId: string) {
        const leaderboardEntries = await this.fetchScores(leaderboardName, instanceId);

        const friendScores: IScoreEntry[] = leaderboardEntries.map(entry => {
            const scoreEntry: IScoreEntry = {
                ...entry,
                score: Number(entry.score),
                rank: Number(entry.rank),
                user: {
                    ...entry.user,
                    handle: entry.user.handle ? entry.user.handle.toString() : undefined,
                    playerId: entry.user.playerId ? entry.user.playerId.toString() : undefined,
                    avatarUrl: entry.user.avatarUrl ? entry.user.avatarUrl.toString() : undefined,
                    isCurrentUser: Boolean(entry.user.isCurrentUser),
                    id: entry.user.id.toString(),
                },
            };
            return scoreEntry;
        });

        return friendScores;
    }

    private async fetchScores(leaderboardName: string, instanceId: string) {
        const friends = await FBInstant.player.getConnectedPlayersAsync();
        const leaderboardEntries = await GBCHandler.fetchLeaderboard(leaderboardName, instanceId);

        const usersByPlayerId = [this.castPlayerToUser(FBInstant.player), ...friends.map(this.castPlayerToUser)].reduce(
            (m, user) => {
                return {
                    ...m,
                    [user.id]: user,
                };
            },
            {} as Record<string, FBInstant.Player>
        );

        const decoratedResults = leaderboardEntries.map(entry => {
            const { user: entryUser } = entry;
            const { playerId, handle } = entryUser;
            const userData: any = usersByPlayerId[String(playerId)] || {};
            return {
                ...entry,
                user: {
                    ...entryUser,
                    handle: userData.name || handle || 'N/A',
                    avatarUrl: userData.avatarUrl,
                    isCurrentUser: playerId === this.getUserId(),
                    playerId,
                },
            };
        });
        return decoratedResults;
    }

    private castPlayerToUser(player: any) {
        return {
            id: player.getID(),
            name: player.getName(),
            avatarUrl: player.getPhoto(),
        };
    }

    public async share(text: string, imageBase64: string, payload?: IMessagePayload, intent?: FBInstant.Intent) {
        if (isIOS()) {
            const base64Image = imageBase64 || (await PayloadHelper.getInviteFriendBase64());
            const playerId: string = await Game.sdk.chooseFriend();
            return await Game.sdk.customUpdate(
                Game.locale.getText('InviteMessage'),
                base64Image,
                playerId,
                {
                    data: PayloadType.INVITE_FRIEND,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                    customEntryPoint: SocialActionTypes.CUSTOM_INVITE_FRIEND,
                },
                'IMMEDIATE',
                Game.locale.getText('InviteCTA')
            );
        } else {
            return await FBInstant.shareAsync({
                intent: intent,
                image: imageBase64,
                text: text,
                data: payload,
            });
        }
    }

    public async createContext(playerId: string): Promise<string> {
        const players = FBContextHandler.getPlayers();
        const playerInContext: boolean = players.some((p: any) => {
            return p === playerId;
        });
        if (!playerInContext) {
            await FBContextHandler.createContextAsync(playerId);
        } else {
            console.log('Player is already in the current context');
        }

        return FBContextHandler.getContextID();
    }

    public async clearPlayerData() {
        return this.saveHandler.clear();
    }

    private async getAdvertisementInstance(
        placement: IAdvertisementPlacement
    ): Promise<FBInterstitialAdOrRewardedVideo> {
        const interstitialPerAdvertisementType: {
            [key in typeof placement['type']]: boolean;
        } = {
            [AdvertisementType.INTERSTITIAL]: true,
            [AdvertisementType.REWARDED]: false,
            [AdvertisementType.WATCH_TO_PLAY]: false,
        };

        const interstitial = interstitialPerAdvertisementType[placement.type];

        track('adType', interstitial ? AdType.INTERSTITIAL : AdType.REWARDED);

        const debugFakeAds = false;

        const instance: FBInterstitialAdOrRewardedVideo = debugFakeAds
            ? FBSDKDriver.debugCreateFakeAd(placement.id)
            : interstitial
            ? await FBInstant.getInterstitialAdAsync(placement.id)
            : await FBInstant.getRewardedVideoAsync(placement.id);
        await instance.loadAsync();

        return instance;
    }

    private static debugCreateFakeAd(placementId: string): FBInterstitialAdOrRewardedVideo {
        const logPrefix = '[Fake ads]';
        return {
            loadAsync: async () => {
                console.log(`${logPrefix} loadAsync start ${placementId}`);
                await sleep(200);
                console.log(`${logPrefix} loadAsync end ${placementId}`);
            },
            showAsync: async () => {
                console.log(`${logPrefix} showAsync start ${placementId}`);
                // WARNING: This does not prevent user from clicking stuff like real ads would do
                await sleep(3000);
                console.log(`${logPrefix} showAsync end ${placementId}`);
            },
        };
    }

    public async displayAdvertisement(state: IGameState, placement: IAdvertisementPlacement, payload: any):Promise<boolean> {
        
        try {
            track('adProvider', 'Facebook');
            track('adSdkVersion', FBInstant.getSDKVersion());
        } catch (e) {
            console.warn('Tracking failed', e);
        }

        trackRequestAdvertisement(state, placement, payload);
        const requestStartTime: number = DateHelper.now.getTime();

        let instance: FBInterstitialAdOrRewardedVideo;
        try {
            instance = await this.getAdvertisementInstance(placement);
        } catch (e) {
            console.warn('Failed to get ad instance', e);
            throw e;
        }

        const requestEndTime: number = DateHelper.now.getTime();
        track('adRequestTimeMs', requestEndTime - requestStartTime);
        track('adSuccess', Number(Boolean(instance)));

        if (!instance) throw new Error('Unable to load advertisement.');

        trackDisplayedAdvertisement(state, placement, payload);
        try {
            const result = await instance.showAsync();
            return Promise.resolve(result);
            /*if (result === false) {
                console.log("AD SHOULD NOT HAVE GIVEN REWARD!!!")
                return Promise.reject();
                
            }else {
                return Promise.resolve(result);
            }*/
           
            
        } catch (e) {
            console.warn('Failed to show ad instance', e);
            throw e;
        }
        
    }

    // Bot

    public async canSubscribeBot() {
        try {
            return FBInstant.player.canSubscribeBotAsync();
        } catch (e) {
            if (e.message === 'User is already subscribed to this game bot.') {
                this._isSubscribedToBot = true;
            } else {
                console.warn('canSubscribeBotAsync error:', e);
            }
            return false;
        }
    }
    public isSubscribedToBot(): boolean {
        return this._isSubscribedToBot;
    }

    public async subscribeBot() {
        return FBInstant.player
            .subscribeBotAsync()
            .then(() => {
                this._isSubscribedToBot = true;
                return true;
            })
            .catch(() => false);
    }

    //

    public getLanguage(): LanguageKey {
        if (!Object.values(LanguageKey).includes(this.settings.locale as any)) {
            console.warn(
                `Locale "${this.settings.locale}" is not yet supported, ` + `falling back to "${defaultLanguageKey}"`
            );
            return defaultLanguageKey;
        }
        return this.settings.locale as LanguageKey;
    }

    // API testing

    public hasRewardedAdSupport = () => isAPISupported('getRewardedVideoAsync');
    public hasInterstitialAdSupport = () => isAPISupported('getInterstitialAdAsync');
    public canSwitchGame = () => isAPISupported('startGameAsync');

    //

    public async switchGame(appID: string, payload: any) {
        return FBInstant.switchGameAsync(appID, {
            originGameId: Game.config.gameID,
            originPlayerId: this.getUserId(),
            promoReward: false,
            ...payload,
        });
    }

    private saveFB(data: Partial<IPlayerData>): Promise<void> {
        return this.saveHandler.save(data);
    }

    private restoreFB(): Promise<IPlayerData> {
        return this.saveHandler.load(playerDataKeysToLoad) as Promise<IPlayerData>;
    }

    public async chooseFriend(): Promise<string> {
        await FBContextHandler.chooseAsync();

        // Sam: I hate that we return the ID here
        return FBContextHandler.getContextID();
    }

    // Payments

    public readonly hasIAPSupport = () => this.fbPayments.hasIAPSupport();
    public readonly purchaseProduct = (productId: string) => {
        return this.fbPayments.purchaseProduct(productId);
    };
    public readonly fetchCatalog = () => this.fbPayments.fetchCatalog();
    public readonly getCatalog = () => this.fbPayments.iapCatalog;
    public readonly waitForPaymentsEnabled = () => this.fbPayments.waitForPaymentsEnabled();
    public readonly resolveUnconsumedPurchases = () => this.fbPayments.resolveUnconsumedPurchases();

    // Shortcut

    public async canCreateShortcut() {
        return FBInstant.canCreateShortcutAsync();
    }

    public async createShortcut() {
        return FBInstant.createShortcutAsync();
    }

    // A2U

    public async initA2U() {
        return await this.createA2UClient().then(client => {
            console.log('A2U Initiated');
            this.A2UClient = client;
            A2UTimes.forEach((time: number, key: string) => {
                this.createA2UNotification({
                    tag: `Inactive-${key}`,
                    receiverId: this.getUserId(),
                    time: new Date(DateHelper.now.getTime() + time),
                    media: 'https://d3d40s6u63df5a.cloudfront.net/assets/1580477594761-A2U_SOFA_1.png',
                    messageBody: {
                        templateType: 'reward',
                        params: {},
                        media: 'https://d3d40s6u63df5a.cloudfront.net/assets/1580477594761-A2U_SOFA_1.png',
                        payload: {
                            entryPoint: 'A2U',
                            messageId: Random.ID(),
                            reward: {
                                type: RewardType.COINS,
                                value: 500,
                            },
                        },
                    },
                });
            });
        });
    }

    private async createA2UClient() {
        const playerInfo = await FBInstant.player.getSignedPlayerInfoAsync();
        const payload = this.getDecryptedEntryPointData();
        const signature = playerInfo.getSignature();
        const trackingFunc = (data: { eventName: any; params: any }) => {
            this.gameTracking(data.eventName, 1, data.params);
        };

        const a2uClient = A2U.createClient({
            gameId: Game.config.gameID,
            signature,
            trackingFunc,
            payload,
        });
        return a2uClient;
    }

    private createA2UNotification(data: {
        tag: string;
        receiverId: string;
        messageBody: A2U.IMessageBody;
        time: Date;
        media: string;
        expireTime?: Date;
    }): void {
        console.log(`Trying to create A2U notification - ${data.tag}`);
        this.A2UClient.createNotification(data).catch((error: unknown) => {
            console.warn('Failed to create A2U notification:', error);
        });
    }

    // Tracking

    public gameTracking(
        event: FBTrackingEvent,
        value: string | number = null,
        parameters: { [key: string]: string } = null
    ): void {
        console.log('[gameTracking]', event, value, parameters);
        const valueToSend = typeof value !== 'number' ? 1 : value || 1;
        try {
            const error = FBInstant.logEvent(event, valueToSend, parameters);
            if (error !== null) throw error;
        } catch (error) {
            console.warn('logEvent failed:', error);
        }
    }

    public async signIn(platform: AuthPlatform, silent: boolean): Promise<IAuthReturnValues> {
        return;
    }

    public signOut(): Promise<void> {
        return;
    }

    public getFacebookAuthToken(): string {
        return '';
    }
    public getFacebookAppScopedId(): string {
        return '';
    }
    public isFacebookConnected(): boolean {
        return false;
    }
}
